import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Sparkles, X } from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";
import { useIsAdmin } from "@/hooks/useIsAdmin";

const FIRST_DELAY_MS = 20 * 1000; // 20s after load
const REPEAT_MS = 5 * 60 * 1000; // every 5 min
const SNOOZE_KEY = "proUpgradeSnoozeUntil";
const SNOOZE_MS = 6 * 60 * 60 * 1000; // 6h snooze (was 24h)

const HIDE_ROUTES = ["/pricing", "/auth", "/onboarding", "/buy-a-cookie", "/cookie-thanks"];

export function ProUpgradePopup() {
  const [open, setOpen] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(
    typeof window !== "undefined" ? window.matchMedia("(min-width: 640px)").matches : false
  );
  
  const { isPro, status, loading } = useSubscription();
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  // Admins never see promo pop-ups
  const suppressed = adminLoading || isAdmin;
  const hasBillableSubscription = isPro && status !== "admin";

  useEffect(() => {
    const mql = window.matchMedia("(min-width: 640px)");
    const handler = (e: MediaQueryListEvent) => setIsLargeScreen(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    if (loading || suppressed || hasBillableSubscription || !isLargeScreen) {
      setOpen(false);
      return;
    }

    let timer: ReturnType<typeof setTimeout>;
    let interval: ReturnType<typeof setInterval>;

    const isSnoozed = () => {
      const until = Number(localStorage.getItem(SNOOZE_KEY) || 0);
      return Date.now() < until;
    };

    const show = () => {
      if (isSnoozed()) return;
      if (HIDE_ROUTES.some((r) => window.location.pathname.startsWith(r))) return;
      setOpen(true);
    };

    timer = setTimeout(() => {
      show();
      interval = setInterval(show, REPEAT_MS);
    }, FIRST_DELAY_MS);

    return () => {
      clearTimeout(timer);
      if (interval) clearInterval(interval);
    };
  }, [loading, suppressed, hasBillableSubscription, isLargeScreen]);

  const dismiss = () => {
    setOpen(false);
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
  };

  if (!open || !isLargeScreen) return null;
  if (loading || suppressed || hasBillableSubscription) return null;
  if (HIDE_ROUTES.some((r) => window.location.pathname.startsWith(r))) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 max-w-xs animate-in slide-in-from-right-4 fade-in duration-300">
      <div className="rounded-2xl border border-primary/40 bg-background/95 backdrop-blur-md shadow-2xl p-4 relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-primary/20 blur-3xl pointer-events-none" />
        <button
          onClick={dismiss}
          className="absolute top-2 right-2 h-6 w-6 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground z-10"
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
        <div className="relative flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center flex-shrink-0">
            <Crown className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-[10px] font-semibold text-primary uppercase tracking-wide">
              <Sparkles className="h-3 w-3" /> Go Pro
            </div>
            <div className="font-semibold text-sm mt-0.5">Try Pro free for 7 days</div>
            <p className="text-xs text-muted-foreground mt-0.5 mb-2">
              300 AI prompts, full podcast library, unlimited saved plans. No charge until trial ends.
            </p>
            <Link
              to="/pricing"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-semibold transition-colors"
            >
              Start free trial
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
